import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { useContactModal } from "../context/ContactModalContext";
import InsightsCarousel from "./InsightsCarousel";
import FullScreenDetailModal from "./FullScreenDetailModal";

import chefarztCaseStudyImage from "../assets/Images/case-studies/chefarzt-rekrutierung-case-study.jpg";
import medizintechnikCaseStudyImage from "../assets/Images/case-studies/medizintechnik-vertrieb-case-study.avif";
import praxisnachfolgeCaseStudyImage from "../assets/Images/case-studies/praxis-nachfolge-case-study.png";
const CTA_CARD_BG = "#121313";
const BTN_CLASS_DARK =
  "mt-4 inline-flex w-fit items-center gap-2 rounded-2xl border-2 border-white bg-transparent px-6 py-3 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-white hover:text-[#121313]";
export default function ClientWorks() {
  const { openContactModal } = useContactModal() || {};
  const handleContactClick = () => {
    setActiveCase(null);
    if (openContactModal) openContactModal();
  };

  const CASE_STUDY_SECTIONS = [
  { key: "problem", label: "Die Ausgangslage / Kundenproblem" },
  { key: "approach", label: "Unser Vorgehen" },
  { key: "promised", label: "Was wir zugesagt haben" },
  { key: "delivered", label: "Was geliefert wurde" },
];

const INSIGHTS = [
  {
    category: "HEALTHCARE RECRUITING",
    type: "CASE STUDY",
    date: "NOVEMBER 3, 2025",
    title: "Erfolgreiche Besetzung einer Chefarztposition",
    excerpt:
      "Durch gezielte Direktansprache identifizierte REPEXO Medical qualifizierte Kandidaten für eine Chefarztposition in einer großen Klinikgruppe. Innerhalb weniger Wochen konnte die Position erfolgreich mit einem erfahrenen Spezialisten besetzt werden.",
    to: "/medical/insights/chefarzt-rekrutierung",
    image: chefarztCaseStudyImage,
    caseStudy: {
      problem:
        "Eine große Klinikgruppe mit mehreren Standorten suchte dringend einen Chefarzt bzw. eine Chefärztin für die Klinik für Innere Medizin. Die Position war über Monate unbesetzt; die bisherige Anzeigen- und Kandidatensuche hatte keine passenden Bewerber mit der erforderlichen Facharztqualifikation, Führungserfahrung und Bereitschaft zum Wechsel gebracht. Der Druck auf die Klinikleitung und die Belegschaft wuchs.",
      approach:
        "REPEXO Medical analysierte die Anforderungen und den verdeckten Arbeitsmarkt, identifizierte gezielt Kandidaten in vergleichbaren Kliniken und sprach diese diskret per Direktansprache an. Es folgten strukturierte Gespräche, fachliche und persönliche Eignungsprüfungen sowie eine enge Abstimmung mit dem Mandanten über alle Kandidaten.",
      promised:
        "Transparente Prozesssteuerung, ausschließlich qualifizierte Kandidaten mit Facharztanerkennung und relevanter Führungserfahrung, termingerechte Vorstellung von Kandidaten und Begleitung bis zur Vertragsunterzeichnung.",
      delivered:
        "Innerhalb von zehn Wochen wurden drei geeignete Kandidaten vorgestellt. Die Klinikgruppe wählte einen erfahrenen Facharzt für Innere Medizin mit langjähriger Oberarzt- und Leitungserfahrung. Die Position wurde erfolgreich besetzt; der Eintritt erfolgte zum vereinbarten Zeitpunkt. Der Mandant war mit Ablauf und Ergebnis sehr zufrieden.",
    },
  },
  {
    category: "HEALTHCARE RECRUITING",
    type: "CASE STUDY",
    date: "OCTOBER 12, 2025",
    title: "Direktansprache für Medizintechnik Spezialisten",
    excerpt:
      "Ein internationaler Medizintechnikhersteller beauftragte REPEXO Medical mit der Suche nach erfahrenen Vertriebsspezialisten. Durch unsere Headhunting-Methodik konnten mehrere qualifizierte Kandidaten identifiziert und erfolgreich vermittelt werden.",
    to: "/medical/insights/medizintechnik-vertrieb",
    image: medizintechnikCaseStudyImage,
    caseStudy: {
      problem:
        "Ein international tätiger Medizintechnikhersteller benötigte für die DACH-Region mehrere erfahrene Vertriebsspezialisten mit Branchenkenntnis im Healthcare-Bereich. Die Stellen waren über klassische Ausschreibungen nur unzureichend besetzbar; die Anforderungen an technisches Verständnis, Vertriebserfahrung und Kundenbeziehungen im medizinischen Umfeld schränkten den aktiven Bewerbermarkt stark ein.",
      approach:
        "Wir kartierten den Markt für Vertriebs- und Außendienstkräfte in der Medizintechnik, identifizierten geeignete Profile bei Wettbewerbern und Zulieferern und sprachen diese gezielt und vertraulich an. Die Auswahl erfolgte über mehrstufige Interviews und eine enge Abstimmung mit dem Mandanten zu fachlichen und kulturellen Kriterien.",
      promised:
        "Diskrete Direktansprache, Vorstellung nur von Kandidaten mit relevanter Vertriebs- bzw. Medizintechnik-Erfahrung, termingerechte Besetzung der vakanten Positionen und professionelle Begleitung bis zum Vertragsabschluss.",
      delivered:
        "Es wurden vier Vertriebsspezialisten für die DACH-Region identifiziert und vermittelt. Alle Kandidaten brachten mehrjährige Erfahrung in der Medizintechnik und im Vertrieb mit. Die Besetzung erfolgte im geplanten Zeitrahmen; der Mandant konnte sein Vertriebsteam wie vorgesehen verstärken und die Zusammenarbeit mit REPEXO für weitere Suchaufträge fortsetzen.",
    },
  },
  {
    category: "HEALTHCARE RECRUITING",
    type: "CASE STUDY",
    date: "SEPTEMBER 18",
    title: "Nachfolgeregelung für eine medizinische Praxis",
    excerpt:
      "REPEXO Medical unterstützte einen Praxisinhaber bei der erfolgreichen Nachfolgeregelung. Durch ein gezieltes Netzwerk und diskrete Kandidatenansprache konnte ein geeigneter Nachfolger gefunden werden.",
    to: "/medical/insights/praxis-nachfolge",
    image: praxisnachfolgeCaseStudyImage,
    caseStudy: {
      problem:
        "Ein langjähriger Praxisinhaber (Facharzt für Allgemeinmedizin) plante seinen altersbedingten Ausstieg und suchte einen Nachfolger bzw. eine Nachfolgerin für Übernahme oder Einstieg in die Praxis. Die Suche über Anzeigen und Bekannte hatte keine passenden Interessenten erbracht; der Inhaber wollte die Praxis in guten Händen wissen und den Übergang rechtzeitig und geordnet regeln.",
      approach:
        "REPEXO Medical nutzte branchenspezifisches Wissen und Kontakte zu Ärztinnen und Ärzten, die an einer Praxisübernahme oder einem Einstieg in eine etablierte Praxis interessiert waren. Die Ansprache erfolgte diskret; die Auswahl und die Verhandlungen (inkl. rechtliche und kaufmännische Aspekte) wurden in enger Abstimmung mit dem Mandanten begleitet.",
      promised:
        "Diskrete Suche nach einem fachlich und persönlich geeigneten Nachfolger, Vorstellung qualifizierter Kandidaten, Unterstützung bei der Strukturierung des Übergangs und Begleitung bis zur Einigung bzw. Vertragsabschluss.",
      delivered:
        "Es wurde ein approbierter Facharzt mit Interesse an Niederlassung und Praxisübernahme gefunden. Die Nachfolgeregelung wurde vertraglich und zeitlich so gestaltet, dass eine reibungslose Übergabe möglich war. Der bisherige Inhaber konnte die Praxis wie geplant übergeben; der Nachfolger führt die Praxis erfolgreich weiter.",
    },
  },
];
  const [activeCase, setActiveCase] = useState(null);

  return (
    <>
      <section className="bg-white py-12">
        <div className="mx-auto container-app">
          {/* Title */}
          <h2 className="text-xl sm:text-3xl md:text-4xl font-semibold md:font-medium text-gray-900">
           Unsere Kundenarbeit in der Gesundheitsbranche
          </h2>

  <div className="mt-10 rounded-[18px] btn-bg p-10 shadow-[0_18px_50px_rgba(0,0,0,0.10)] sm:p-12">
  <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-16">
    
    {/* Left stat */}
    <div>
      {/* <div className="text-[48px] font-light tracking-[-0.02em] text-neutral-900 sm:text-[56px]">
        Direkt
      </div> */}
      <div className="mt-2 text-[20px] text-neutral-900/90">
        Headhunting
      </div>

      <p className="mt-6 max-w-[520px] text-[14px] leading-6 text-neutral-900/75">
        Unser Kerngeschäft ist die{" "}
        <span className="font-semibold text-neutral-900">
          Direktansprache von qualifizierten Fach- und Führungskräften
        </span>. 
        Durch gezielte Marktanalysen identifizieren wir geeignete Kandidaten 
        direkt in den Branchen unserer Kunden und sprechen diese diskret an, 
        um sie für neue berufliche Herausforderungen zu gewinnen.
      </p>
    </div>

    {/* Right stat */}
    <div>
      {/* <div className="text-[48px] font-light tracking-[-0.02em] text-neutral-900 sm:text-[56px]">
        Healthcare
      </div> */}
      <div className="mt-2 text-[20px] text-neutral-900/90">
        Spezialisten
      </div>

      <p className="mt-6 max-w-[520px] text-[14px] leading-6 text-neutral-900/75">
        REPEXO Medical ist spezialisiert auf die Rekrutierung von{" "}
        <span className="font-semibold text-neutral-900">
          medizinischen Experten, Fachärzten und Spezialisten im Healthcare-Bereich
        </span>. 
        Wir unterstützen sowohl mittelständische Unternehmen als auch 
        internationale Konzerne dabei, die passenden Talente für ihre 
        anspruchsvollen Positionen zu finden.
      </p>
    </div>

  </div>
</div>
        </div>
      </section>
      <InsightsCarousel
        INSIGHTS={INSIGHTS}
        Heading=""
        BtnText=""
        onOpenDetail={(item) => setActiveCase(item)}
      />

      {/* Case study popup using shared modal */}
      <FullScreenDetailModal
        open={!!activeCase}
        onClose={() => setActiveCase(null)}
        title={activeCase?.title}
        image={activeCase?.image}
        meta={activeCase ? `${activeCase.type} · ${activeCase.date}` : undefined}
        sections={
          activeCase?.caseStudy
            ? [
                ...CASE_STUDY_SECTIONS.map((s) => ({
                  id: `modal-case-${s.key}`,
                  label: s.label.split(" / ")[0],
                })),
                { id: "modal-case-contact", label: "Kontakt" },
              ]
            : activeCase
              ? [
                  { id: "modal-case-content", label: "Inhalt" },
                  { id: "modal-case-contact", label: "Kontakt" },
                ]
              : []
        }
      >
        {activeCase && (
          <div id="modal-case-content" className="mt-0 w-full">
            {activeCase.caseStudy ? (
              <>
                {CASE_STUDY_SECTIONS.map(({ key, label }) => {
                  const text = activeCase.caseStudy[key];
                  if (!text) return null;
                  const leftLabel = label.split(" / ")[0].toUpperCase();
                  return (
                    <section
                      key={key}
                      id={`modal-case-${key}`}
                      className="border-b border-gray-100 py-10 md:py-12 first:pt-0"
                    >
                      <div className="flex flex-col gap-6 md:flex-row md:gap-10 lg:gap-12">
                        <div className="flex-shrink-0 md:w-36 lg:w-40">
                          <span className="detail-side-label text-xs font-semibold uppercase tracking-[0.2em] text-gray-700">
                            {leftLabel}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 max-w-4xl">
                          <h3 className="text-2xl font-semibold leading-snug text-gray-900 md:text-3xl">
                            {label.split(" / ")[0]}
                          </h3>
                          <p className="mt-3 text-sm leading-relaxed text-gray-700 md:text-base">
                            {text}
                          </p>
                        </div>
                      </div>
                    </section>
                  );
                })}
                <section id="modal-case-contact" className="border-b border-gray-100 py-10 md:py-12">
                  <div className="flex flex-col gap-6 md:flex-row md:gap-10 lg:gap-12">
                    <div className="flex-shrink-0 md:w-36 lg:w-40">
                      <span className="detail-side-label text-xs font-semibold uppercase tracking-[0.2em] text-gray-700">
                        KONTAKT
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 max-w-4xl">
                      <div
                        className="rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.08)] md:p-8"
                        style={{ backgroundColor: CTA_CARD_BG }}
                      >
                        <h3 className="text-xl font-semibold text-white md:text-2xl">
                          Haben Sie eine ähnliche Herausforderung?
                        </h3>
                        <p className="mt-2 text-sm text-white/90 md:text-base">
                          Nehmen Sie Kontakt mit uns auf - wir besprechen gerne Ihr Anliegen.
                        </p>
                        <button
                          type="button"
                          className={"group " + BTN_CLASS_DARK}
                          onClick={handleContactClick}
                        >
                          Jetzt Kontakt aufnehmen
                          <ArrowRight className="h-5 w-6 shrink-0 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden />
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              </>
            ) : (
              <>
                <section className="border-b border-gray-100 py-10 md:py-12">
                  <div className="flex flex-col gap-6 md:flex-row md:gap-10 lg:gap-12">
                    <div className="flex-shrink-0 md:w-36 lg:w-40">
                      <span className="detail-side-label text-xs font-semibold uppercase tracking-[0.2em] text-gray-700">
                        INHALT
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 max-w-4xl">
                      <p className="text-sm leading-relaxed text-gray-700 md:text-base">
                        {activeCase.excerpt}
                      </p>
                      <p className="mt-4 text-sm leading-relaxed text-gray-700 md:text-base">
                        In diesem Fallbeispiel zeigen wir, wie REPEXO Medical mit
                        diskreter Direktansprache und tiefem Marktverständnis die
                        passenden Kandidaten identifiziert und über den gesamten Prozess
                        begleitet hat - von der ersten Kontaktaufnahme bis zur
                        erfolgreichen Besetzung.
                      </p>
                    </div>
                  </div>
                </section>
                <section id="modal-case-contact" className="border-b border-gray-100 py-10 md:py-12">
                  <div className="flex flex-col gap-6 md:flex-row md:gap-10 lg:gap-12">
                    <div className="flex-shrink-0 md:w-36 lg:w-40">
                      <span className="detail-side-label text-xs font-semibold uppercase tracking-[0.2em] text-gray-700">
                        KONTAKT
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 max-w-4xl">
                      <div
                        className="rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.08)] md:p-8"
                        style={{ backgroundColor: CTA_CARD_BG }}
                      >
                        <h3 className="text-xl font-semibold text-white md:text-2xl">
                          Haben Sie eine ähnliche Herausforderung?
                        </h3>
                        <p className="mt-2 text-sm text-white/90 md:text-base">
                          Nehmen Sie Kontakt mit uns auf - wir besprechen gerne Ihr Anliegen.
                        </p>
                        <button
                          type="button"
                          className={"group " + BTN_CLASS_DARK}
                          onClick={handleContactClick}
                        >
                          Jetzt Kontakt aufnehmen
                          <ArrowRight className="h-5 w-6 shrink-0 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden />
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        )}
      </FullScreenDetailModal>
    </>
  );
}

